import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JTable } from '@atoms/JTable';
import { Box, Stack } from '@mui/material';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { useGetJobs } from '@jobs/queries/useGetJobs';
import JobRow from '../JobRow';
import { JSelect } from '@atoms/JSelect';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import { useDebouncedCallback } from 'use-debounce';
import { useGetEnterprisesSuggestions } from '@enterprise/queries/useGetEnterprises';
import { useGetEstablishmentsSuggestions } from '@establishment/queries/useGetEstablishment';
import IOption from '@models/option';
import { useCities } from '@/queries/useCities';
import JobStatusEnum from '@jobs/models/job-status-enum';
import { SearchTextField } from '@atoms/SearchTextField';
import JDatePicker from '@atoms/JDatePicker';
import useJobs from '@jobs/hooks/useJobs';
import { appendSearchFilter } from '@lib/tableFilterUtils';

const TABLE_COLS: TableColumn[] = [
  { name: 'JOB', orderable: true, sortingField: 'job.title' },
  {
    name: 'ENTERPRISE',
    orderable: true,
    sortingField: 'enterprise.name',
  },
  {
    name: 'ESTABLISHMENT',
    orderable: true,
    sortingField: 'establishment.name',
  },
  { name: 'CREATION_DATE', orderable: true, sortingField: 'job.createdAt' },
  { name: 'REFERENCE', orderable: true, sortingField: 'job.referenceNumber' },
  {
    name: 'MISSION_START',
    orderable: true,
    sortingField: 'job.jobStartDate',
  },
  {
    name: 'MISSION_END',
    orderable: true,
    sortingField: 'job.jobEndDate',
  },
  { name: 'STATUS', orderable: true, sortingField: 'job.deletedAt' },
];

const PER_PAGE = 20;
const DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'job.createdAt',
};

const JobsList: React.FC = () => {
  const { t } = useTranslation();
  const { setJobsCount } = useJobs();
  const [page, setPage] = useState<number>();
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(DEFAULT_ORDER);
  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');
  const [establishmentInputValue, setEstablishmenInputValue] =
    useState<string>('');

  const { data } = useGetJobs(
    {
      page,
      limit: PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );
  const { data: cities } = useCities(10, 1, cityInputValue);
  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );
  const { data: establishments } = useGetEstablishmentsSuggestions(
    10,
    1,
    establishmentInputValue,
  );

  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.itemCount;
  const isNotFound = rows?.length === 0;

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    1000,
  );
  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);
  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 1000);
  const debouncedEstablishment = useDebouncedCallback((value) => {
    setEstablishmenInputValue(value);
  }, 1000);

  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const establishmentsOptions = establishments
    ? establishments.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];

  useEffect(() => {
    setJobsCount(meta?.itemCount);
  }, [meta?.itemCount]);
  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          options={[
            { label: 'Tous', value: '' },
            ...Object.keys(JobStatusEnum).map((item: string) => ({
              label: t(item),
              value: item.toLocaleLowerCase(),
            })),
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_STATUS,
              event.target.value as string,
              searchFilter,
              setSearchFilter,
            )
          }
        />

        <SearchTextField
          label={t('TITLE') ?? ''}
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.JOB_TITLE,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
        />

        <SearchTextField
          label={t('REFERENCE') ?? ''}
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.JOB_REFERENCE_NUMBER,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
        />
        <JAutocomplete
          label={t('ESTABLISHMENT')}
          options={establishmentsOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ESTABLISHMENT_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            )
          }
          onTextChange={(val) => debouncedEstablishment(val)}
        />

        <JAutocomplete
          label={t('ENTERPRISE')}
          options={enterprisesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            )
          }
          onTextChange={(val) => debouncedEnterprise(val)}
        />

        <JAutocomplete
          label={t('CITY_ZIP_CODE')}
          options={citiesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_CITY_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            )
          }
          onTextChange={(val) => debouncedCity(val)}
        />

        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_PUBLICATION_START_DATE,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_PUBLICATION_START_DATE,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('JOB_PUBLUCATION_START_DATE')}
        />
        <JDatePicker
          onClear={() =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_PUBLICATION_END_DATE,
              '',
              searchFilter,
              setSearchFilter,
            )
          }
          handleChange={(value) =>
            appendSearchFilter(
              FilterCriteriaFields.JOB_PUBLICATION_END_DATE,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
          label={t('JOB_PUBLUCATION_END_DATE')}
        />
      </Stack>
      <JTable
        isNotFound={isNotFound}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_JOB_FOUND') ?? ''}
        total={pagesCount ?? 0}
        perpage={PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <JobRow row={row} key={row.id} />
        ))}
      </JTable>

      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default JobsList;
